/**
 * Digitain Partner API — panel tarafının ortak parçaları.
 *
 * Sağlayıcının iki yüzeyi vardır ve panel ikisini birden kullanır: oyun ve
 * para akışı (`/api/sportdigi/...`, ayar kartı Oyun Kontrol → Spor & Cashout
 * altında) ile self-servis yönetim (`/partner/v1/*`, bizde
 * `/admin/sportdigi/partner/...`). Yollar backend'de sabittir; buradaki
 * sarmalayıcılar yalnızca panelin tekrar tekrar aynı adresi yazmasını önler.
 *
 * İki kural bu dosyanın tamamını belirler (EveryMatrix'teki aynısı):
 *
 * - **Sağlayıcının reddi olduğu gibi gösterilir.** Yönetim uçlarının hatası
 *   sabit bir koda bağlıdır (`partner_api_disabled`, `invalid_signature`,
 *   `422 invalid_risk`) ve tek bir "işlem başarısız" cümlesine indirgenirse
 *   yönetici olmayan bir anahtar arızasını aramaya başlar.
 * - **`ignored_fields` sessiz kalmaz.** Tanınmayan bir alan uygulanmaz ve
 *   cevapta bildirilir — okunmazsa yönetici korunduğunu sanır.
 */
import client from '../../lib/api';

const BASE = '/admin/sportdigi';
const PARTNER = `${BASE}/partner`;

/** Sağlayıcı/HTTP hatasının okunabilir metni. */
export const errorText = (error, fallback = 'İstek başarısız') => (
  error?.response?.data?.detail || error?.message || fallback
);

/**
 * Yazma cevabındaki "uygulanmadı" listesi.
 *
 * Sağlayıcı tanımadığı alanı **sessizce** atar ve adını `ignored_fields`
 * içinde döndürür; panel bunu yazmazsa yazım hatası olan bir limit kaydedildi
 * sanılır ve kurulum korumasız kalır.
 */
export const ignoredFields = (data) => {
  const rows = data?.ignored_fields;
  return Array.isArray(rows) ? rows.filter(Boolean).map(String) : [];
};

/** JSON metnini nesneye çevirir; hatayı okunur bir cümleye dönüştürür. */
export const parseJson = (text, fallback = {}) => {
  const raw = String(text || '').trim();
  if (!raw) return { value: fallback, error: '' };
  try {
    const value = JSON.parse(raw);
    if (value === null || typeof value !== 'object') {
      return { value: null, error: 'Gövde bir JSON nesnesi olmalıdır' };
    }
    return { value, error: '' };
  } catch (error) {
    return { value: null, error: `Geçersiz JSON: ${error.message}` };
  }
};

/** Nesneyi düzenlenebilir metne çevirir (iki boşluk girinti). */
export const toJsonText = (value) => {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
};

export const partnerApi = {
  config: () => client.get(`${PARTNER}/config`),
  summary: (params) => client.get(`${PARTNER}/summary`, { params }),
  bets: (params) => client.get(`${PARTNER}/bets`, { params }),
  bet: (betId) => client.get(`${PARTNER}/bets/${encodeURIComponent(betId)}`),
  retryCredit: (betId) => client.post(
    `${PARTNER}/bets/${encodeURIComponent(betId)}/retry-credit`),
  alerts: (params) => client.get(`${PARTNER}/alerts`, { params }),
  flags: (params) => client.get(`${PARTNER}/flags`, { params }),
  catalog: (sport) => client.get(`${PARTNER}/catalog`, {
    params: sport ? { sport } : undefined,
  }),
  events: (params) => client.get(`${PARTNER}/events`, { params }),
  matchesLive: () => client.get(`${PARTNER}/matches/live`),
  match: (eventId) => client.get(
    `${PARTNER}/matches/${encodeURIComponent(eventId)}`),
  markets: (sport) => client.get(`${PARTNER}/markets`, { params: { sport } }),

  settings: () => client.get(`${PARTNER}/settings`),
  saveSettings: (body) => client.patch(`${PARTNER}/settings`, body),
  saveVisibility: (body) => client.put(`${PARTNER}/visibility`, body),
  cashout: () => client.get(`${PARTNER}/cashout`),
  saveCashout: (body) => client.put(`${PARTNER}/cashout`, body),
  riskConfig: () => client.get(`${PARTNER}/risk-config`),
  saveRiskConfig: (body) => client.put(`${PARTNER}/risk-config`, body),
  riskRules: () => client.get(`${PARTNER}/risk-rules`),
  saveRiskRule: (body) => client.put(`${PARTNER}/risk-rules`, body),
  deleteRiskRule: (id) => client.delete(
    `${PARTNER}/risk-rules/${encodeURIComponent(id)}`),
  riskAudit: () => client.get(`${PARTNER}/risk-audit`),

  themePresets: () => client.get(`${PARTNER}/theme-presets`),
  theme: () => client.get(`${PARTNER}/theme`),
  saveTheme: (body) => client.patch(`${PARTNER}/theme`, body),

  tradingLive: (sport) => client.get(`${PARTNER}/trading/live`, {
    params: sport ? { sport } : undefined,
  }),
  tradingMarkets: (fixture) => client.get(`${PARTNER}/trading/markets`, {
    params: { fixture },
  }),
  overrides: () => client.get(`${PARTNER}/trading/overrides`),
  saveOverride: (body) => client.post(`${PARTNER}/trading/overrides`, body),
  patchOverride: (id, body) => client.patch(
    `${PARTNER}/trading/overrides/${encodeURIComponent(id)}`, body),
  deleteOverride: (id) => client.delete(
    `${PARTNER}/trading/overrides/${encodeURIComponent(id)}`),
  tradingBulk: (body) => client.post(`${PARTNER}/trading/bulk`, body),

  brands: () => client.get(`${PARTNER}/brands`),
  createBrand: (body) => client.post(`${PARTNER}/brands`, body),
  saveBrand: (id, body) => client.patch(
    `${PARTNER}/brands/${encodeURIComponent(id)}`, body),
  deleteBrand: (id) => client.delete(
    `${PARTNER}/brands/${encodeURIComponent(id)}`),

  webhooks: () => client.get(`${PARTNER}/webhooks`),
  saveWebhooks: (body) => client.patch(`${PARTNER}/webhooks`, body),
  webhookQueue: () => client.get(`${PARTNER}/webhooks/queue`),
  alertChannel: () => client.get(`${PARTNER}/alerts-channel`),
  saveAlertChannel: (body) => client.patch(`${PARTNER}/alerts-channel`, body),

  // Bonus & freebet — Digitain'e özgü yüzey (EveryMatrix partner API'sinde
  // yoktur): kampanya tanımı, oyuncuya hak tanımı ve iptal.
  bonusOffers: (params) => client.get(`${PARTNER}/bonuses/offers`, { params }),
  createOffer: (body) => client.post(`${PARTNER}/bonuses/offers`, body),
  saveOffer: (id, body) => client.patch(
    `${PARTNER}/bonuses/offers/${encodeURIComponent(id)}`, body),
  bonusGrants: (params) => client.get(`${PARTNER}/bonuses/grants`, { params }),
  grantBonus: (body) => client.post(`${PARTNER}/bonuses/grants`, body),
  cancelGrant: (id) => client.post(
    `${PARTNER}/bonuses/grants/${encodeURIComponent(id)}/cancel`),
  playerBonuses: (playerId) => client.get(
    `${PARTNER}/bonuses/players/${encodeURIComponent(playerId)}`),

  rotateKey: () => client.post(`${PARTNER}/key-rotate`),
};

/**
 * Ödemenin durumu — sağlayıcının kümesi.
 *
 * `dead` yönetici için **eylem gerektiren** tek değerdir: sağlayıcı on sekiz
 * denemeden sonra vazgeçer ve ödeme kendiliğinden bir daha çıkmaz.
 */
export const CREDIT_STATUS_LABELS = {
  none: 'Ödeme yok',
  pending: 'Kuyrukta',
  failed: 'Başarısız',
  done: 'Ödendi',
  dead: 'Ölü — elle kuyruğa alın',
};

export const CREDIT_STATUS_TONE = {
  none: 'text-slate-400',
  pending: 'text-sky-300',
  failed: 'text-amber-300',
  done: 'text-emerald-300',
  dead: 'text-rose-400',
};

/** Ödeme yeniden kuyruğa alınabilir mi (yalnızca başarısız/ölü). */
export const canRetryCredit = (status) => (
  status === 'failed' || status === 'dead'
);

/** Bonus türleri — sağlayıcının kümesi (serbest, yatırım eşleşmesi, …). */
export const BONUS_TYPES = [
  { value: 'freebet', label: 'Freebet' },
  { value: 'deposit_match', label: 'Yatırım eşleşmesi' },
  { value: 'cashback', label: 'Cashback' },
  { value: 'risk_free', label: 'Kayıp iadesi' },
];
